import React, { useState, useEffect } from 'react';
import { X, Sparkles, ArrowLeft, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';

export default function CreateProjectOverlay({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showDiscardConfirm) {
          setShowDiscardConfirm(false);
        } else {
          handleCloseAttempt();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showDiscardConfirm, name, description]);

  const handleCloseAttempt = () => {
    if (name.trim() || description.trim()) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  const handleCreate = async () => {
    if (!name.trim() || isCreating) return;

    setIsCreating(true);
    try {
      // Pick a random color from palette
      const colors = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#6366f1', '#ec4899', '#14b8a6'];
      const color = colors[Math.floor(Math.random() * colors.length)];

      const response = await api.post('/projects', {
        name: name.trim(),
        description: description.trim(),
        type: 'product',
        color,
        icon: 'folder'
      });

      toast.success('Project created successfully!');
      onCreated(response.data);
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error('Failed to create project');
      setIsCreating(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[100] flex flex-col bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl animate-in fade-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-6 md:px-12">
          {/* Left side */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleCloseAttempt}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              disabled={isCreating}
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="font-medium">Projects</span>
            </button>
            <div className="h-4 w-px bg-black/10 dark:bg-white/10" />
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
              Create New Project
            </span>
          </div>

          {/* Right side */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCloseAttempt}
            disabled={isCreating}
            className="rounded-full"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-6 md:px-12 py-8 md:py-16 flex flex-col gap-10">
            {/* Name section */}
            <div className="flex flex-col gap-3 animate-fade-up">
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
                Project Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Give your project a name..."
                className="w-full bg-transparent text-4xl md:text-5xl font-bold text-foreground placeholder:text-muted-foreground/30 border-none focus:outline-none focus:ring-0 p-0"
                autoFocus
                disabled={isCreating}
              />
            </div>

            {/* Divider */}
            <div
              className="h-px bg-black/5 dark:bg-white/5 w-full animate-fade-in"
              style={{ animationDelay: '100ms' }}
            />

            {/* Description section */}
            <div
              className="flex flex-col gap-3 animate-fade-up"
              style={{ animationDelay: '150ms' }}
            >
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
                Description & Links
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your project and paste any relevant links.&#10;&#10;For example:&#10;Building a new marketing website...&#10;&#10;https://slack.com/channel/website-redesign&#10;https://github.com/org/website-repo&#10;&#10;We'll automatically detect links and connect them as sources."
                className="w-full min-h-[300px] bg-transparent text-lg text-foreground/90 placeholder:text-muted-foreground/40 border-none focus:outline-none focus:ring-0 p-0 resize-none leading-relaxed"
                disabled={isCreating}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-6 md:px-12 py-8 flex flex-col items-center gap-4 bg-gradient-to-t from-white via-white to-transparent dark:from-neutral-950 dark:via-neutral-950 animate-fade-up"
          style={{ animationDelay: '200ms' }}
        >
          <Button
            size="lg"
            onClick={handleCreate}
            disabled={!name.trim() || isCreating}
            className="rounded-full px-8 h-12 text-base shadow-xl shadow-primary/20 transition-all hover:scale-105 active:scale-95 min-w-[200px]"
          >
            {isCreating ? (
              <>
                <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                <span>Create Project</span>
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground">
            Anchor will analyze your links and set up sources automatically
          </p>
        </div>
      </div>

      {/* Discard Confirmation Modal */}
      {showDiscardConfirm && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/20 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4 border border-white/10 animate-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-500" />
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-lg font-semibold text-foreground">
                  Discard project?
                </h3>
                <p className="text-sm text-muted-foreground">
                  You have unsaved changes. Are you sure you want to discard this project?
                </p>
              </div>
              <div className="flex gap-3 w-full mt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDiscardConfirm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={onClose}
                  className="flex-1"
                >
                  Discard
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
